




























































































  import { defineComponent, ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
  import LazyHydrate from 'vue-lazy-hydration'
  import SeoMethodsMixin, { DIVIDER } from '../mixins/SeoMethodsMixin'
  import { StickyStack } from '~/plugins/stickystack'
  import useCurrentInstance from '~/utils/useCurrentInstance'
  import { buildCanonicalUrl } from '~/utils/seo/canonical'
  import { useCompareStore } from '~/stores/useCompareStore'
  import OneHidePriceSwitcherComponent from '~/components/organisms/grid/header/OneHidePriceSwitcherComponent.vue'

  interface MetaInfo {
    title: string
    titleTemplate: (titleChunk: string) => string
    htmlAttrs?: {
      lang?: string
      [key: string]: any
    }
    meta: Array<{
      charset?: string
      hid?: string
      name?: string
      content?: string | null
      property?: string
      [key: string]: any
    }>
    link: Array<{
      rel?: string
      type?: string
      href?: string | null
      sizes?: string
      pbody?: boolean
      as?: string
      hid?: string
      [key: string]: any
    }>
    script: Array<{
      type?: string
      src?: string
      json?: any
      rel?: string
      body?: boolean
      async?: boolean
      defer?: boolean
      hid?: string
      [key: string]: any
    }>
    noscript: Array<{
      innerHTML?: string
      hid?: string
      [key: string]: any
    }>
  }

  enum HeadTagsTypes {
    link = 'link',
    script = 'script',
    meta = 'meta',
    noscript = 'noscript',
  }

  export default defineComponent({
    name: 'MainLayout',
    components: {
      OneAvailableNotificationUpdate: () => import('~/components/molecules/notifications/OneAvailableNotificationUpdate.vue'),
      OneHeaderGridComponent: () => import('~/components/organisms/header/OneHeaderGridComponent.vue'),
      OneFooter: () => import('~/components/organisms/footer/OneFooter.vue'),
      OneOfflineNotification: () => import('~/components/molecules/notifications/OneOfflineNotification.vue'),
      OneButtonToggle: () => import('~/components/molecules/buttons/OneButtonToggle.vue'),
      MobileMenuWrapper: () => import('~/components/molecules/menus/MobileMenuWrapper.vue'),
      MobileAccountMenuWrapper: () => import('~/components/molecules/menus/MobileAccountMenuWrapper.vue'),
      OneBreadcrumbs: () => import('~/components/molecules/breadcrumbs/OneBreadcrumbs.vue'),
      OneCookieBanner: () => import('~/components/molecules/banners/OneCookieBanner.vue'),
      OneNoticeBanner: () => import('~/components/molecules/banners/OneNoticeBanner.vue'),
      OneInvoiceBanner: () => import('~/components/molecules/banners/OneInvoiceBanner.vue'),
      OneFullScreenPluginModal: () => import('~/components/molecules/modals/OneFullScreenPluginModal.vue'),
      OneCmsYoutubeComponent: () => import('~/components/organisms/grid/shared/OneCmsYoutubeComponent.vue'),
      LazyHydrate,
      OneHidePriceSwitcherComponent,
    },
    mixins: [SeoMethodsMixin],

    setup() {
      const vm = useCurrentInstance()
      const compareStore = useCompareStore()
      const DEFAULT_NOTIF_DURATION = 6000
      const updateCallback = ref<Function | null>(null)
      let stack: StickyStack | null = null
      const isHovered = ref(false)

      const tenantKey = computed(() => vm.$store.state.tenantKey)
      const menuAccountShow = computed(() => vm.$store.state.layout.menuAccountShow)
      const menuMobileShow = computed(() => vm.$store.state.layout.menuMobileShow)
      const menuFiltersShow = computed(() => vm.$store.state.layout.menuFiltersShow)
      const breadcrumbs = computed(() => vm.$store.state.layout.breadcrumbs)
      const showBreadcrumbs = computed(() => vm.$store.state.layout.showBreadcrumbs)
      const backdrop = computed(() => vm.$store.state.layout.backdrop)
      const isPriceGross = computed(() => vm.$store.state.layout.priceGross)
      const isIdle = computed(() => vm.$store.state.idle.isIdle)

      const configuration = computed(() => vm.$store.getters['cms/configuration'])
      const logo = computed(() => vm.$store.getters['cms/logo'])
      const getClient = computed(() => vm.$store.getters['account/getClient'])
      const isCompareEnabled = computed(() => vm.$store.getters['cms/configuration']?.showCompareProducts)

      const isPricesSwitcherExists = computed(() => {
        const config = configuration.value
        if (!config) {
          return false
        }

        const checkForPricesSwitcher = (items: any): boolean => {
          if (!items || !Array.isArray(items)) {
            return false
          }

          for (const item of items) {
            if (item?.type === 'pricesSwitcher') {
              return true
            }

            if (item?.elements && Array.isArray(item.elements)) {
              if (checkForPricesSwitcher(item.elements)) {
                return true
              }
            }
          }

          return false
        }

        return checkForPricesSwitcher(config.header?.elements)
      })

      const fetchHeavyData = () => vm.$store.dispatch('fetchHeavyData')
      const toggleMenuAccount = (val: boolean) => vm.$store.commit('layout/TOGGLE_ACCOUNT_MENU', val)
      const toggleMobileMenu = (val: boolean) => vm.$store.commit('layout/TOGGLE_MOBILE_MENU', val)
      const setPriceGross = (val: boolean) => vm.$store.commit('layout/SET_PRICE_GROSS', val)
      const backdropRun = (val: boolean) => vm.$store.commit('layout/TOGGLE_BACKDROP', val)

      const isMobileOrTablet = computed(() => vm.$utils.isMobileOrTablet(vm.$mq))

      const logoData = computed(() => configuration.value?.logo?.img)

      const backdropCategory = computed(() => ({
        booleanValue: backdrop.value,
        uid: 'category',
      }))

      const comparedProducts = computed(() => compareStore.products)
      const compareCount = computed(() => compareStore.compareCount)

      const defaultSeoTitleTemplate = (titleChunk: string): string | null => {
        return titleChunk || (vm as any).defaultSeoData.title
      }

      const onUpdateAnswer = (accepted: boolean) => {
        if (updateCallback.value) {
          updateCallback.value(accepted)
          updateCallback.value = null
        }
      }

      const showUpdatePrompt = (cb: Function) => {
        updateCallback.value = cb
        vm.$notify({
          group: 'pwaUpdate',
          duration: -1,
        })
      }

      const initializeStickyStack = () => {
        nextTick(() => {
          stack = new StickyStack()
        })
      }

      const onCategoryClick = (categoryId: string | null) => {
        if (categoryId) {
          const categoryPath: string | null = vm.$routes.getCategoryPath(categoryId)
          if (categoryPath) {
            vm.$router.push(categoryPath, () => {
              toggleMobileMenu(false)
            })
          }
        }
      }

      const menuAccountClose = () => {
        if (menuAccountShow.value) {
          toggleMenuAccount(false)
        }
      }

      const closeMobileMenuAfterSelected = () => {
        if (process.client) {
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }
        menuAccountClose()
      }

      const extractResourceId = (url: string): string => {
        if (!url) {
          return 'default'
        }
        const timestampMatch = url.match(/-(\d{13,})-?/)
        if (timestampMatch && timestampMatch[1]) {
          return timestampMatch[1]
        }
        const versionMatch = url.match(/[?&]v=([^&]+)/)
        if (versionMatch && versionMatch[1]) {
          return versionMatch[1]
        }
        return btoa(url).substring(0, 10)
      }

      const setHeadTags = (header: MetaInfo) => {
        try {
          const headTags = configuration.value?.globalTags?.tagsHeadHtml
          if (headTags == null || JSON.parse(headTags)?.tags == null) return

          const tagsObject = JSON.parse(headTags)
          if (tagsObject.tags == null) return

          const tenantScriptsOff = vm.$route.query?.tenantScriptsOff === 'true'
          const currentTenantKey = tenantKey.value || 'default'

          tagsObject.tags.forEach(({ type, attributes }: { type: HeadTagsTypes; attributes: any }, index: number) => {
            if (type === 'script' && tenantScriptsOff) return

            const resourceUrl = attributes.src || attributes.href || ''
            const resourceId = extractResourceId(resourceUrl) || index
            const tagHid = attributes.hid || `tenant-${currentTenantKey}-${type}-${resourceId}`

            const tagWithHid = {
              ...attributes,
              hid: tagHid,
            }

            header[type] = header[type] ? [...header[type], tagWithHid] : [tagWithHid]
          })
        } catch (error) {
          vm.$logger.warn('[LAYOUT - setHeadTags]', error)
        }
      }

      const setCanonicalLink = (header: MetaInfo) => {
        try {
          const origin = vm.$store.getters.origin
          if (!origin) {
            return
          }

          header.link.push({
            hid: 'canonical',
            rel: 'canonical',
            href: buildCanonicalUrl(origin, vm.$route.path, vm.$route.query),
          } as any)
        } catch (error) {
          vm.$logger.warn('[LAYOUT - setCanonicalLink]', error)
        }
      }

      const setLangAttribute = (header: MetaInfo) => {
        try {
          if (vm.$tenantConfig.configuration?.lang) {
            header.htmlAttrs = { lang: vm.$tenantConfig.configuration.lang }
          }
        } catch (error) {
          vm.$logger.warn('[LAYOUT - setLangAttribute]', error)
        }
      }

      const setTenantIcons = (header: MetaInfo) => {
        try {
          if (vm.$tenantConfig.configuration?.theme.themeIcons) {
            const currentTenantKey = tenantKey.value || 'default'
            const href = vm.$tenantConfig.configuration.theme.themeIcons.css
            const iconsHid = `tenant-${currentTenantKey}-theme-icons-${extractResourceId(href)}`

            header.link.push({
              hid: iconsHid,
              rel: 'stylesheet',
              href: href,
              pbody: true,
            } as any)
          }
        } catch (error) {
          vm.$logger.warn('[LAYOUT - setTenantIcons]', error)
        }
      }

      const setTenantCss = (header: MetaInfo) => {
        try {
          const config = vm.$tenantConfig.configuration as any

          if (config?.globalCssUrl) {
            const currentTenantKey = tenantKey.value || 'default'
            const href = config.globalCssUrl
            const cssHid = `tenant-${currentTenantKey}-global-css-${extractResourceId(href)}`

            header.link.push({
              hid: cssHid,
              rel: 'stylesheet',
              href: href,
              pbody: true,
            } as any)
          }
        } catch (error) {
          vm.$logger.warn('[LAYOUT - setTenantCss]', error)
        }
      }

      const setScripts = (header: MetaInfo) => {
        try {
          const config = vm.$tenantConfig.configuration as any
          const globalScripts = config?.globalScripts
          const tenantScriptsOff = vm.$route.query?.tenantScriptsOff === 'true'

          if (!globalScripts || tenantScriptsOff) return

          const headerScript = globalScripts.scriptsHeadUrl
          const bodyScript = globalScripts.scriptsBodyUrl
          const { async, defer } = globalScripts
          const currentTenantKey = tenantKey.value || 'default'

          if (headerScript) {
            header.script.push({
              hid: `tenant-${currentTenantKey}-script-head-${extractResourceId(headerScript)}`,
              rel: 'script',
              body: false,
              src: headerScript,
              async,
              defer,
            })
          }

          if (bodyScript) {
            header.script.push({
              hid: `tenant-${currentTenantKey}-script-body-${extractResourceId(bodyScript)}`,
              rel: 'script',
              body: true,
              src: bodyScript,
              async,
              defer,
            })
          }
        } catch (error) {
          vm.$logger.warn('[LAYOUT - setScripts]', error)
        }
      }

      const setPwaAttributes = (header: MetaInfo) => {
        try {
          if (vm.$pwa?.isEnabled) {
            header.link = [
              ...header.link,
              { rel: 'manifest', href: '/api/manifest.json' },
              { rel: 'apple-touch-icon', href: vm.$pwa.appleIconUrl, sizes: '512x512' },
            ]
            header.meta.push({
              name: 'theme-color',
              content: vm.$lib.primary,
            })
          }
        } catch (error) {
          vm.$logger.warn('[LAYOUT - setPwaAttributes]', error)
        }
      }

      watch(isIdle, (newVal) => {
        if (!newVal && vm.$auth.isAuthenticated) {
          vm.$api.webhooks.app.publishClientLogged({
            externalId: getClient.value.externalId,
          })
        }
      })

      watch(menuFiltersShow, (newVal) => {
        if (process.client) {
          const layoutOverflow = newVal ? 'hidden' : 'unset'
          const layoutEl = document.getElementById('__layout')
          if (layoutEl) layoutEl.style.overflowX = layoutOverflow
        }
      }, { immediate: true })

      watch(menuMobileShow, (newVal) => {
        if (process.client) {
          const layoutOverflow = newVal ? 'hidden' : 'unset'
          const layoutEl = document.getElementById('__layout')
          if (layoutEl) layoutEl.style.overflowX = layoutOverflow
        }
      }, { immediate: true })

      watch(() => vm.$route.path, (newPath) => {
        if (process.client) {
          document.body.className = newPath.substring(1).replace(/\//g, '-').replace(/-\s*$/, '') || 'index'
        }
      }, { immediate: true })

      onMounted(async () => {
        if (process.client) {
          if (vm.$pwa) vm.$pwa.$on('pwa:update:prompt', showUpdatePrompt)
          initializeStickyStack()
          await fetchHeavyData()
        }
      })

      onUnmounted(() => {
        if (process.client && vm.$pwa) {
          vm.$pwa.$off('pwa:update:prompt', showUpdatePrompt)
        }
      })

      return {
        DEFAULT_NOTIF_DURATION,
        isHovered,
        tenantKey,
        menuAccountShow,
        menuMobileShow,
        menuFiltersShow,
        breadcrumbs,
        showBreadcrumbs,
        backdrop,
        isPriceGross,
        isIdle,
        configuration,
        logo,
        getClient,
        isCompareEnabled,
        isPricesSwitcherExists,
        isMobileOrTablet,
        logoData,
        backdropCategory,
        comparedProducts,
        compareCount,
        toggleMenuAccount,
        toggleMobileMenu,
        setPriceGross,
        backdropRun,
        onUpdateAnswer,
        onCategoryClick,
        menuAccountClose,
        closeMobileMenuAfterSelected,
        defaultSeoTitleTemplate,
        setHeadTags,
        setCanonicalLink,
        setLangAttribute,
        setTenantIcons,
        setTenantCss,
        setScripts,
        setPwaAttributes,
      }
    },
    head() {
      const vm = this as any
      const defaultHeader: MetaInfo = {
        title: vm.structuredSeoTitle(DIVIDER.DOT, [vm.defaultSeoData.title]),
        titleTemplate: (titleChunk: string) => vm.defaultSeoTitleTemplate(titleChunk),
        meta: [
          { charset: 'utf-8' },
          { name: 'viewport', content: 'width=device-width, minimum-scale=1, maximum-scale=1' },
          {
            hid: 'description',
            name: 'description',
            content: vm.structuredSeoTitle(DIVIDER.DASH, [vm.defaultSeoData.description]),
          },
          { hid: 'author', name: 'author', content: 'Unity Group' },
          { hid: 'robots', name: 'robots', content: vm.defaultSeoRobots },
        ],
        link: [
          {
            rel: 'icon',
            type: 'image/x-icon',
            href: (vm.logo && vm.logo.favicon) || 'favicon.png',
          },
        ],
        script: [
          vm.breadcrumbs.length ? {
            type: 'application/ld+json',
            json: {
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: vm.breadcrumbs.map((item: any, index: number) => {
                return {
                  '@type': 'ListItem',
                  position: index + 1,
                  name: item.name,
                  item: item.link,
                }
              }),
            },
          } : {},
        ],
        noscript: [],
      }

      vm.setLangAttribute(defaultHeader)
      vm.setTenantIcons(defaultHeader)
      vm.setPwaAttributes(defaultHeader)
      vm.setTenantCss(defaultHeader)
      vm.setScripts(defaultHeader)
      vm.setHeadTags(defaultHeader)
      vm.setCanonicalLink(defaultHeader)

      return defaultHeader
    },
  } as any)
